/**
 * The evidence of a form escalation question (agion/Agion#3437 C6), opened
 * on request through POST /v1/streaming/run-output/open, which reads the run
 * again as the viewer. The text lives in the component that shows it only:
 * never in a store, browser storage or a log. An answer that arrives after
 * the evidence was closed or opened again is dropped.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { Escalation } from '../types/escalations'
import type { EvidenceRef, EvidenceView } from '../services/escalation-form'
import { evidenceRefOf, evidenceView } from '../services/escalation-form'
import { openRunOutput, runOutputOpenRefusalOf, type RunOutputOpenRefusal } from '../services/run-output-open'

export type EscalationEvidenceState =
  | { status: 'closed' }
  | { status: 'opening' }
  | { status: 'open'; view: EvidenceView }
  | { status: 'refused'; refusal: RunOutputOpenRefusal }

export function useEscalationEvidence(ref: EvidenceRef) {
  const [state, setState] = useState<EscalationEvidenceState>({ status: 'closed' })
  const attempt = useRef(0)

  useEffect(
    () => () => {
      attempt.current++
    },
    [],
  )

  const open = useCallback(async () => {
    const mine = ++attempt.current
    setState({ status: 'opening' })
    try {
      const content = await openRunOutput(ref)
      if (attempt.current === mine) setState({ status: 'open', view: evidenceView(content) })
    } catch (error) {
      // The refusal carries a code only; the evidence is never in it.
      if (attempt.current === mine) setState({ status: 'refused', refusal: runOutputOpenRefusalOf(error) })
    }
  }, [ref])

  const close = useCallback(() => {
    attempt.current++
    setState({ status: 'closed' })
  }, [])

  return { state, open, close }
}

/**
 * The evidence an escalation names (evidenceRefOf), the same object while
 * its four ids stay the same, so a card that re-reads its escalation does
 * not open the evidence again.
 */
export function useEvidenceRef(escalation: Escalation): EvidenceRef | undefined {
  const named = evidenceRefOf(escalation)
  const key = named ? [named.mission_id, named.execution_id, named.host_id, named.artifact_id].join('\n') : ''
  // The four ids are the reference's identity.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  return useMemo(() => named, [key])
}

/**
 * The evidence opened at once, as the in-thread question card shows it
 * (agion/Agion#3446, challenge 2026-10-26-escalation-question-chat-card
 * C3a): through the same run-output open as Open evidence, as the viewer.
 */
export function useOpenedEscalationEvidence(ref: EvidenceRef): EscalationEvidenceState {
  const { state, open } = useEscalationEvidence(ref)
  useEffect(() => {
    void open()
  }, [open])
  return state
}
